import axios from 'axios';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { getWxJwt } from 'utils/wxJwtToken';

import useWxJwtReady from './useWxJwtReady';

// The navbar's product list: whether the company has Cue at all.
const PRODUCT_ACCESS_PATH =
  '/api/servicesecurity/processes/users/productFeatureAccess';
// Cue's own per-user gate: the caller's `copilot#consume_credits` permission,
// then the company's Cue credit balance. A 403 means the role lacks it.
const CUE_GATE_CHECK_PATH =
  '/api/serviceorganizations/processes/companyCredits/gateCheck/cue';

interface ProductFeatureAccessEntry {
  key: string;
  enabled: boolean;
}

/**
 * Whether this user may ask Cue a question: the account is on WX, its
 * company has Cue, and the user may spend Cue credits. False until both WX
 * answers arrive and on any failure, so Ask Cue never sends a question Cue
 * would refuse. Calls WX with the WX JWT through plain axios, not
 * utils/axios, whose 401 interceptor signs the recruiter out of Hire.
 */
export const useCanAskCue = (): boolean => {
  const whoami = useSelector(makeSelectWhoami());
  const isJwtReady = useWxJwtReady();
  const [canAskCue, setCanAskCue] = useState(false);

  const isOnWx = Boolean(whoami?.account_wx_external_id);
  const wxApiUrl: string | undefined = whoami?.account_wx_api_url;

  useEffect(() => {
    setCanAskCue(false);
    const token = isOnWx && wxApiUrl && isJwtReady ? getWxJwt() : null;
    if (!token) return undefined;

    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${wxApiUrl}${PRODUCT_ACCESS_PATH}`, { headers }),
      axios.get(`${wxApiUrl}${CUE_GATE_CHECK_PATH}`, { headers }),
    ])
      .then(([access, gate]) => {
        if (cancelled) return;
        const products: ProductFeatureAccessEntry[] =
          access.data?.data?.productAccess ?? [];
        const hasCue = products.some(
          product => product.key === 'cue' && product.enabled,
        );
        setCanAskCue(hasCue && gate.data?.data?.gated === false);
      })
      .catch(() => {
        if (!cancelled) setCanAskCue(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOnWx, wxApiUrl, isJwtReady]);

  return canAskCue;
};

export default useCanAskCue;
